import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/axios';
import { useAuth } from '../../context/AuthContext';
import { AI_JD_OFF_MESSAGE, isAiJdEnabled } from '../../utils/entitlements';
import {
  resolveRoleKeyForPosition,
  autoFetchJobFamilyForPosition,
  ensureRoleSeatedInJobFamily,
  setPositionJobFamilyKey,
  rememberLastCreatedRoleKey,
  syncJobFamilySkillsToJd,
  isValidRoleKey,
  ensurePositionSkillWeightages,
  setJobFamilyAiEnabled,
} from '../../data/jobFamilyMaster';
import { JobFamilyTripleSelect } from './JobFamilyPanel';
import {
  formatJdPreviewHtml,
  normalizeJdForStorage,
  normalizeJdHtmlFromSource,
  normalizeJdForExport,
  JD_PREVIEW_INNER_CSS,
} from '../../utils/jdPreviewFormat';

const STYLES_BASE = `
/* ── Drag handle ── */
.npm-drag-handle { cursor: grab; }
.npm-drag-handle:active { cursor: grabbing; }

/* ── Typography helpers ── */
.npm-lbl { font-size:11px; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--ds-text); margin-bottom:7px; display:block; }

/* ── Inputs ── */
.npm-inp { width:100%; background:var(--ds-input-bg); border:1.5px solid var(--ds-border); border-radius:9px; padding:10px 13px; font-size:13px; color:var(--ds-text); outline:none; font-family:'Plus Jakarta Sans',sans-serif; transition:border-color 0.18s,box-shadow 0.18s; box-sizing:border-box; }
.npm-inp:focus { border-color:#0041C2; box-shadow:0 0 0 3px rgba(0,65,194,0.12); }
.npm-inp.err { border-color:#EF4444; box-shadow:none; }
.npm-inp[readonly] { background:var(--ds-surface); cursor:not-allowed; color:var(--ds-text); opacity:1; }
.npm-inp::placeholder, .npm-textarea::placeholder, .npm-sel::placeholder { color:var(--ds-muted) !important; opacity:1 !important; }
.npm-sel { width:100%; background:var(--ds-input-bg); border:1.5px solid var(--ds-border); border-radius:9px; padding:10px 13px; font-size:13px; color:var(--ds-text); outline:none; font-family:'Plus Jakarta Sans',sans-serif; appearance:none; cursor:pointer; transition:border-color 0.18s; box-sizing:border-box; }
.npm-sel:focus { border-color:#0041C2; box-shadow:0 0 0 3px rgba(0,65,194,0.12); }
.npm-sel option { background:var(--ds-card); color:var(--ds-text); }
.npm-err { font-size:11px; color:#EF4444; margin-top:4px; }

/* ── Ceipal job list ── */
.npm-job-list { max-height:210px; overflow-y:auto; border:1.5px solid var(--ds-border); border-radius:9px; background:var(--ds-surface); }
.npm-job-row { display:block; width:100%; text-align:left; border:none; border-bottom:1px solid var(--ds-border); padding:10px 15px; cursor:pointer; transition:background 0.15s; background:transparent; color:var(--ds-text); font-family:'Plus Jakarta Sans',sans-serif; }
.npm-job-row:last-child { border-bottom:none; }
.npm-job-row:hover { background:var(--ds-hover); }
.npm-job-row.sel { background:var(--ds-active); border-left:3px solid #0041C2; }

/* ── Tabs ── */
.npm-tab-bar { display:flex; gap:8px; margin-bottom:20px; padding:4px; background:var(--ds-surface); border-radius:10px; border:1px solid var(--ds-border); width:fit-content; }
.npm-tab { padding:9px 20px; border-radius:7px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid transparent; color:var(--ds-text2); background:transparent; transition:all 0.18s; font-family:'Plus Jakarta Sans',sans-serif; }
.npm-tab.on { background:var(--ds-card); border-color:var(--ds-border2); color:#1A5FD4; box-shadow:0 2px 6px rgba(0,65,194,0.12); }

/* ── JD preview + edit textarea (same height so no layout shift) ── */
.npm-jd-preview { background:var(--ds-input-bg); border:1.5px solid var(--ds-border); border-radius:9px; padding:16px; font-size:12px; color:var(--ds-text); height:270px; overflow-y:auto; line-height:1.55; box-sizing:border-box; text-align:left; }
.npm-textarea { width:100%; background:var(--ds-input-bg); border:1.5px solid var(--ds-border); border-radius:9px; padding:12px 14px; font-size:12px; color:var(--ds-text); outline:none; resize:none; font-family:monospace; line-height:1.8; transition:border-color 0.18s; box-sizing:border-box; }
.npm-textarea:focus { border-color:#0041C2; box-shadow:0 0 0 3px rgba(0,65,194,0.1); }
.npm-textarea.edit-h { height:270px; }
.npm-textarea.regen-h { height:130px; resize:vertical; }

/* ── Buttons ── */
.npm-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border-radius:9px; font-size:12px; font-weight:600; cursor:pointer; transition:all 0.18s; border:1.5px solid; font-family:'Plus Jakarta Sans',sans-serif; white-space:nowrap; }
.npm-btn:disabled { opacity:0.45; cursor:not-allowed !important; transform:none !important; }
.npm-btn-primary { background:linear-gradient(135deg,#0041C2,#1A5FD4); color:#fff !important; border-color:transparent; box-shadow:0 0 14px rgba(0,65,194,0.3); }
.npm-btn-primary:hover:not(:disabled) { box-shadow:0 0 28px rgba(0,65,194,0.5); transform:translateY(-1px); }
.npm-btn-secondary { background:transparent; color:var(--ds-text2); border-color:var(--ds-border2); }
.npm-btn-secondary:hover:not(:disabled) { border-color:#1A5FD4; color:#1A5FD4; background:var(--ds-hover); }
.npm-btn-ghost { background:transparent; color:var(--ds-muted); border-color:var(--ds-border); }
.npm-btn-ghost:hover:not(:disabled) { color:var(--ds-text); border-color:var(--ds-border2); background:var(--ds-hover); }
.npm-btn-danger { background:transparent; color:#EF4444; border-color:rgba(239,68,68,0.4); }
.npm-btn-danger:hover:not(:disabled) { background:rgba(239,68,68,0.06); }

/* ── Step indicator ── */
.npm-steps { display:flex; align-items:center; gap:0; padding:16px 24px 0; background:var(--ds-card); }
.npm-step-wrap { display:flex; align-items:center; gap:8px; }
.npm-step-dot { width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; transition:all 0.25s; flex-shrink:0; }
.npm-step-dot.done { background:#10B981; color:#fff; }
.npm-step-dot.active { background:#0041C2; color:#fff; box-shadow:0 0 0 4px rgba(0,65,194,0.2); }
.npm-step-dot.idle { background:var(--ds-surface); border:1.5px solid var(--ds-border); color:var(--ds-muted); }
.npm-step-label { font-size:12px; font-weight:600; font-family:'Plus Jakarta Sans',sans-serif; white-space:nowrap; }
.npm-step-line { flex:1; height:1.5px; background:var(--ds-border); min-width:20px; }

/* ── Form grid ── */
.npm-form-grid { display:flex; flex-direction:column; gap:16px; }
.npm-row-2 { display:flex; gap:14px; }
.npm-row-2 > div { flex:1; min-width:0; }
.npm-section { margin-bottom:20px; }

/* ── Diff view ── */
.npm-diff-col { flex:1; border:1.5px solid var(--ds-border); border-radius:9px; overflow:hidden; min-width:0; }
.npm-diff-head { padding:10px 14px; font-size:11px; font-weight:700; border-bottom:1px solid var(--ds-border); }
.npm-diff-body { padding:6px 0; font-size:11px; height:240px; overflow-y:auto; color:var(--ds-text); line-height:1.75; font-family:monospace; }
.npm-diff-line { display:flex; align-items:flex-start; padding:1px 12px; white-space:pre-wrap; word-break:break-word; }
.npm-diff-line.removed { background:rgba(239,68,68,0.13); border-left:3px solid rgba(239,68,68,0.55); padding-left:9px; }
.npm-diff-line.added   { background:rgba(234,179,8,0.13);  border-left:3px solid rgba(234,179,8,0.55);  padding-left:9px; }
.npm-diff-line.same    { border-left:3px solid transparent; padding-left:9px; }
.npm-diff-sign { font-size:10px; font-weight:700; margin-right:5px; flex-shrink:0; line-height:1.75; user-select:none; }

/* ── Drop zone ── */
.npm-dropzone { border:2px dashed var(--ds-border2); border-radius:10px; padding:32px 20px; text-align:center; cursor:pointer; transition:all 0.2s; }
.npm-dropzone:hover,.npm-dropzone.drag { border-color:#0041C2; background:rgba(0,65,194,0.04); }

/* ── Confirm summary ── */
.npm-confirm-row { display:flex; gap:14px; padding:10px 0; border-bottom:1px solid var(--ds-border); }
.npm-confirm-row:last-child { border-bottom:none; }
.npm-confirm-lbl { font-size:12px; color:var(--ds-text2); width:130px; flex-shrink:0; font-weight:500; }
.npm-confirm-val { font-size:12px; font-weight:700; color:var(--ds-text); word-break:break-word; }

/* ── Pagination ── */
.npm-pg-btns { display:flex; align-items:center; gap:6px; justify-content:center; margin-top:9px; }
.npm-pg-btn { padding:4px 12px; border-radius:7px; border:1.5px solid var(--ds-border); background:transparent; cursor:pointer; font-size:11px; font-weight:600; color:var(--ds-text2); font-family:'Plus Jakarta Sans',sans-serif; transition:all 0.15s; }
.npm-pg-btn:hover:not(:disabled) { border-color:#1A5FD4; color:#1A5FD4; background:var(--ds-hover); }
.npm-pg-btn:disabled { opacity:0.3; cursor:not-allowed; }
.npm-pg-btn.cur { background:#0041C2; color:#fff; border-color:#0041C2; }

/* ── Ceipal picker table ── */
.cej-tbl { border:1.5px solid var(--ds-border); border-radius:10px; overflow:hidden; }
.cej-head { display:grid; grid-template-columns:120px 1fr 130px 90px 110px; background:var(--ds-surface); padding:10px 16px; border-bottom:1px solid var(--ds-border); }
.cej-th { font-size:11px; font-weight:700; color:var(--ds-text2); text-transform:uppercase; letter-spacing:0.06em; }
.cej-row { display:grid; grid-template-columns:120px 1fr 130px 90px 110px; padding:12px 16px; border-bottom:1px solid var(--ds-border); align-items:center; transition:background 0.15s; }
.cej-row:last-child { border-bottom:none; }
.cej-row:hover { background:var(--ds-hover); }
.cej-row.used-row { opacity:0.6; }
.cej-code { display:inline-block; background:#0041C2; color:#fff; border-radius:6px; padding:4px 9px; font-size:11px; font-weight:700; font-family:monospace; white-space:nowrap; }
.cej-ftab { padding:5px 16px; border-radius:20px; border:1.5px solid var(--ds-border); cursor:pointer; font-size:12px; font-weight:600; background:transparent; color:var(--ds-text2); transition:all 0.18s; font-family:'Plus Jakarta Sans',sans-serif; }
.cej-ftab.on { background:#0041C2; color:#fff; border-color:#0041C2; }
.cej-sel-btn { padding:6px 14px; border-radius:7px; border:1.5px solid #0041C2; background:transparent; color:#0041C2; cursor:pointer; font-size:11px; font-weight:700; transition:all 0.18s; font-family:'Plus Jakarta Sans',sans-serif; white-space:nowrap; }
.cej-sel-btn:hover:not(:disabled) { background:#0041C2; color:#fff; }
.cej-sel-btn.sel-active { background:#0041C2; color:#fff; }
.cej-sel-btn:disabled { opacity:0.5; cursor:not-allowed; border-color:var(--ds-border); color:var(--ds-muted); }

/* ── Details highlight ── */
@keyframes cej-pulse { 0%,100%{box-shadow:0 0 0 3px rgba(0,65,194,0.25);} 50%{box-shadow:0 0 0 6px rgba(0,65,194,0.12);} }
.cej-details-highlight { border:2px solid rgba(0,65,194,0.4); border-radius:12px; padding:16px; background:rgba(0,65,194,0.03); animation:cej-pulse 1.6s ease-in-out 2; }
`;

const STYLES = STYLES_BASE + JD_PREVIEW_INNER_CSS;

function wordJaccard(a, b) {
  const aw = new Set((a || '').toLowerCase().split(/\s+/).filter(Boolean));
  const bw = new Set((b || '').toLowerCase().split(/\s+/).filter(Boolean));
  let common = 0;
  aw.forEach(w => { if (bw.has(w)) common++; });
  const union = aw.size + bw.size - common;
  return union === 0 ? 1 : common / union;
}

function computeWordDiff(aLine, bLine) {
  const tok = s => (s || '').split(/(\s+)/);
  const aT = tok(aLine), bT = tok(bLine);
  const m = aT.length, n = bT.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = aT[i-1] === bT[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);
  const segs = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aT[i-1] === bT[j-1]) { segs.push({ type: 'same', text: aT[i-1] }); i--; j--; }
    else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) { segs.push({ type: 'added', text: bT[j-1] }); j--; }
    else { segs.push({ type: 'removed', text: aT[i-1] }); i--; }
  }
  return segs.reverse();
}

function computeLineDiff(aText, bText) {
  const aLines = (aText || '').split('\n');
  const bLines = (bText || '').split('\n');
  const m = aLines.length, n = bLines.length;

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = aLines[i-1].trim() === bLines[j-1].trim()
        ? dp[i-1][j-1] + 1
        : Math.max(dp[i-1][j], dp[i][j-1]);
    }
  }

  const raw = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aLines[i-1].trim() === bLines[j-1].trim()) {
      raw.push({ type: 'same', aLine: aLines[i-1], bLine: bLines[j-1] }); i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
      raw.push({ type: 'added', line: bLines[j-1] }); j--;
    } else {
      raw.push({ type: 'removed', line: aLines[i-1] }); i--;
    }
  }
  raw.reverse();

  // Post-process: pair consecutive removed+added blocks; merge similar pairs into 'changed'
  const ops = [];
  let k = 0;
  while (k < raw.length) {
    if (raw[k].type === 'same') { ops.push(raw[k]); k++; continue; }
    const removedBlock = [], addedBlock = [];
    while (k < raw.length && (raw[k].type === 'removed' || raw[k].type === 'added')) {
      if (raw[k].type === 'removed') removedBlock.push(raw[k].line);
      else addedBlock.push(raw[k].line);
      k++;
    }
    const minLen = Math.min(removedBlock.length, addedBlock.length);
    for (let p = 0; p < minLen; p++) {
      if (wordJaccard(removedBlock[p], addedBlock[p]) > 0.45) {
        ops.push({ type: 'changed', aLine: removedBlock[p], bLine: addedBlock[p], wordDiff: computeWordDiff(removedBlock[p], addedBlock[p]) });
      } else {
        ops.push({ type: 'removed', line: removedBlock[p] });
        ops.push({ type: 'added',   line: addedBlock[p] });
      }
    }
    for (let p = minLen; p < removedBlock.length; p++) ops.push({ type: 'removed', line: removedBlock[p] });
    for (let p = minLen; p < addedBlock.length;   p++) ops.push({ type: 'added',   line: addedBlock[p] });
  }

  const leftDiff = ops.filter(o => o.type !== 'added').map(o =>
    o.type === 'changed'
      ? { type: 'changed', wordDiff: o.wordDiff }
      : { type: o.type, line: o.aLine ?? o.line }
  );
  const rightDiff = ops.filter(o => o.type !== 'removed').map(o =>
    o.type === 'changed'
      ? { type: 'changed', wordDiff: o.wordDiff }
      : { type: o.type, line: o.bLine ?? o.line }
  );
  return { leftDiff, rightDiff };
}

function generateJobCode(posName) {
  const words = (posName || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const abbrev = words.map(w => w[0].toUpperCase()).slice(0, 4).join('');
  const num = String(100 + Math.floor(Math.random() * 900));
  return `HIC - ${abbrev}-${num}`;
}

export default function NewPositionModal({ show, onHide, onSuccess, source, currentUser }) {
  const navigate = useNavigate();
  // ── Navigation ────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1); // 1 | 2 | 3

  // ── Step 1 fields ─────────────────────────────────────────────────────────
  const [positionName, setPositionName]   = useState('');
  const [clientName, setClientName]       = useState('');
  const [jobCode, setJobCode]             = useState('');
  const [aboutCompany, setAboutCompany]   = useState('');
  const [fieldErrors, setFieldErrors]     = useState({});
  const [selectedJobFamilyKey, setSelectedJobFamilyKey] = useState('');
  const [editingConfirmJobFamily, setEditingConfirmJobFamily] = useState(false);
  const jobFamilyTouchedRef = useRef(false);

  // ── Ceipal ────────────────────────────────────────────────────────────────
  const [ceipalJobs, setCeipalJobs]             = useState([]);
  const [ceipalLoading, setCeipalLoading]       = useState(false);
  const [ceipalSearch, setCeipalSearch]         = useState('');
  const [ceipalPage, setCeipalPage]             = useState(1);
  const [ceipalTotalPages, setCeipalTotalPages] = useState(1);
  const [ceipalTotal, setCeipalTotal]           = useState(0);
  const [ceipalPageSize, setCeipalPageSize]     = useState(20);
  const [ceipalFilter, setCeipalFilter]         = useState('all'); // 'all'|'available'|'used'
  const [usedJobCodes, setUsedJobCodes]         = useState(new Set());
  const [selectedCeipalJob, setSelectedCeipalJob] = useState(null);
  const searchTimerRef = useRef(null);

  // ── ATS (Workday / Greenhouse) ─────────────────────────────────────────────
  const [atsJobs, setAtsJobs]                   = useState([]);
  const [atsLoading, setAtsLoading]             = useState(false);
  const [atsSearch, setAtsSearch]               = useState('');
  const [atsPage, setAtsPage]                   = useState(1);
  const [atsTotalPages, setAtsTotalPages]       = useState(1);
  const [atsTotal, setAtsTotal]                 = useState(0);
  const [atsCompanyFilter, setAtsCompanyFilter] = useState('all');
  const [atsCompanies, setAtsCompanies]         = useState([]);
  const [selectedAtsJob, setSelectedAtsJob]     = useState(null);
  const [fetchingAtsJd, setFetchingAtsJd]       = useState(false);
  const atsSearchTimerRef = useRef(null);

  // ── Step 2: JD creation ───────────────────────────────────────────────────
  // Craft JD (jd_craft) off = AI off only; manual write / edit / upload always work.
  const { entitlements, unrestricted } = useAuth();
  const aiJdOn = isAiJdEnabled(entitlements, { unrestricted });
  // Job family suggestions on this screen follow Craft JD's AI switch.
  setJobFamilyAiEnabled(aiJdOn);
  const defaultJdTab = aiJdOn ? 'ai' : 'manual';
  const [jdTab, setJdTab]                     = useState(defaultJdTab);
  const [manualJdText, setManualJdText]       = useState('');
  const [jdContent, setJdContent]             = useState('');
  const [jdType, setJdType]                   = useState('');
  const [fetchingCeipalJd, setFetchingCeipalJd] = useState(false);

  // AI form fields
  const [aiJobRole, setAiJobRole]           = useState('');
  const [aiExperience, setAiExperience]     = useState('');
  const [aiLocation, setAiLocation]         = useState('');
  const [aiQuickInput, setAiQuickInput]     = useState('');
  const [aiPositionType, setAiPositionType] = useState('Permanent Position');
  const [aiCompanyName, setAiCompanyName]   = useState('');
  const [generatingJd, setGeneratingJd]     = useState(false);
  const [aiFormErrors, setAiFormErrors]     = useState({});

  // File upload
  const [selectedFile, setSelectedFile]   = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragActive, setDragActive]       = useState(false);
  const fileInputRef = useRef(null);

  // JD editing
  const [isEditing, setIsEditing]     = useState(false);
  const [editContent, setEditContent] = useState('');

  // Regeneration
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenComment, setRegenComment]     = useState('');
  const [regenerating, setRegenerating]     = useState(false);
  const [originalJd, setOriginalJd]         = useState('');
  const [regenJd, setRegenJd]               = useState('');
  const [showDiff, setShowDiff]             = useState(false);

  // Create
  const [creating, setCreating] = useState(false);

  // Step 3: Job Family profile (resolved from role + JD + experience; non-blocking)
  const [confirmJfRoleKey, setConfirmJfRoleKey] = useState('');
  const [confirmJfPreview, setConfirmJfPreview] = useState(null);
  const [confirmJfLoading, setConfirmJfLoading] = useState(false);
  const [confirmJfCreated, setConfirmJfCreated] = useState(false);
  const [confirmJfFamilyCreated, setConfirmJfFamilyCreated] = useState(false);
  const confirmJfRequestRef = useRef(0);

  // Drag
  const [dragPos, setDragPos]   = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragOffRef  = useRef({ x: 0, y: 0 });
  const dialogRef   = useRef(null);
  const detailsRef  = useRef(null);

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!show) return;
    setStep(1);
    setPositionName(''); setClientName(''); setJobCode(''); setAboutCompany(''); setFieldErrors({});
    setSelectedJobFamilyKey('');
    setEditingConfirmJobFamily(false);
    jobFamilyTouchedRef.current = false;
    setCeipalJobs([]); setCeipalSearch(''); setCeipalPage(1); setCeipalTotalPages(1); setCeipalTotal(0);
    setCeipalFilter('all'); setUsedJobCodes(new Set());
    setSelectedCeipalJob(null);
    setAtsJobs([]); setAtsSearch(''); setAtsPage(1); setAtsTotalPages(1); setAtsTotal(0);
    setAtsCompanyFilter('all'); setAtsCompanies([]); setSelectedAtsJob(null); setFetchingAtsJd(false);
    setJdTab(defaultJdTab); setManualJdText(''); setJdContent(''); setJdType(''); setFetchingCeipalJd(false);
    setIsEditing(false); setEditContent('');
    setShowRegenModal(false); setRegenComment(''); setOriginalJd(''); setRegenJd(''); setShowDiff(false);
    setSelectedFile(null); setDragActive(false);
    setAiJobRole(''); setAiExperience(''); setAiLocation(''); setAiQuickInput('');
    setAiPositionType('Permanent Position'); setAiCompanyName(''); setAiFormErrors({});
    setCreating(false);
    setConfirmJfRoleKey('');
    setConfirmJfPreview(null);
    setConfirmJfLoading(false);
    setConfirmJfCreated(false);
    setConfirmJfFamilyCreated(false);
    confirmJfRequestRef.current = 0;
    if (source === 'ceipal') { loadCeipalJobs(1, '', 20); loadUsedJobCodes(); }
    if (source === 'workday' || source === 'greenhouse') { loadAtsCompanies(source); loadAtsJobs(1, '', 'all', source); }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPositionExperience = () =>
    aiExperience?.trim() ||
    selectedCeipalJob?.experience ||
    selectedCeipalJob?.min_experience ||
    '';

  // Auto-fetch Job Family via AI on Step 3 (Confirm) once role + JD are available.
  useEffect(() => {
    if (!show || step !== 3 || !positionName.trim() || !jdContent.trim()) return;

    const requestId = ++confirmJfRequestRef.current;
    setConfirmJfLoading(true);
    setConfirmJfRoleKey('');
    setConfirmJfPreview(null);
    setConfirmJfCreated(false);
    setConfirmJfFamilyCreated(false);

    const timer = setTimeout(() => {
      autoFetchJobFamilyForPosition({
        title: positionName.trim(),
        jdText: jdContent,
        experience: getPositionExperience(),
        generateSkills: true,
      })
        .then((result) => {
          if (confirmJfRequestRef.current !== requestId) return;
          setConfirmJfRoleKey(result.roleKey || '');
          setConfirmJfPreview(result.roleKey ? null : (result.preview || null));
          setConfirmJfCreated(Boolean(result.created));
          setConfirmJfFamilyCreated(Boolean(result.familyCreated));
          if (result.roleKey) {
            setSelectedJobFamilyKey(result.roleKey);
            rememberLastCreatedRoleKey(result.roleKey);
          }
        })
        .catch((err) => {
          if (confirmJfRequestRef.current !== requestId) return;
          console.warn('Job family auto-fetch failed:', err);
          setConfirmJfRoleKey('');
          setConfirmJfPreview(null);
        })
        .finally(() => {
          if (confirmJfRequestRef.current === requestId) setConfirmJfLoading(false);
        });
    }, 500);

    return () => clearTimeout(timer);
  }, [show, step, positionName, jdContent, aiExperience, selectedCeipalJob]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Position dialog in content area (right of sidebar ~260px) ─────────────
  useEffect(() => {
    if (!show) { setDragPos(null); setDragging(false); return; }
    const SIDEBAR = 260;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxW = (source === 'ceipal' || source === 'workday' || source === 'greenhouse') ? 940 : 820;
    const mw = Math.min(maxW, (vw - SIDEBAR) * 0.95);
    setDragPos({ x: SIDEBAR + ((vw - SIDEBAR - mw) / 2), y: Math.max(24, (vh - 640) / 2) });
  }, [show]);

  // ── Blur right pane when modal is open ─────────────────────────────────────
  useEffect(() => {
    const rightPane = document.querySelector('.app-main-content')?.parentElement;
    if (!rightPane) return;
    rightPane.style.transition = 'filter 0.2s ease';
    rightPane.style.filter = show ? 'blur(6px) brightness(0.72)' : '';
    return () => { rightPane.style.filter = ''; };
  }, [show]);

  // ── Drag mouse listeners ───────────────────────────────────────────────────
  useEffect(() => {
    if (!dragging) return;
    const SIDEBAR = 260;
    const onMove = (e) => {
      if (!dialogRef.current) return;
      const mw = dialogRef.current.offsetWidth;
      const mh = dialogRef.current.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setDragPos({
        x: Math.max(SIDEBAR, Math.min(vw - mw - 8, e.clientX - dragOffRef.current.x)),
        y: Math.max(0,       Math.min(vh - mh - 8, e.clientY - dragOffRef.current.y)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  const onDragStart = (e) => {
    if (e.button !== 0 || e.target.closest('button,input,select,textarea,a')) return;
    const rect = dialogRef.current.getBoundingClientRect();
    dragOffRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
    e.preventDefault();
  };

  // ── Ceipal helpers ─────────────────────────────────────────────────────────
  const loadCeipalJobs = async (page, search, ps) => {
    const size = ps ?? ceipalPageSize;
    setCeipalLoading(true);
    try {
      const resp = await api.get(
        `/ceipal/job-postings?page=${page}&pageSize=${size}&search=${encodeURIComponent(search || '')}`
      );
      if (resp.data?.success) {
        setCeipalJobs(resp.data.data || []);
        const total = resp.data.totalCount || 0;
        setCeipalTotal(total);
        setCeipalTotalPages(Math.max(1, Math.ceil(total / size)));
        setCeipalPage(page);
      }
    } catch { toast.error('Failed to load Ceipal jobs'); }
    finally { setCeipalLoading(false); }
  };

  const loadUsedJobCodes = async () => {
    try {
      const resp = await api.get('/positions?limit=2000');
      const list = resp.data?.data || resp.data?.positions || resp.data || [];
      const codes = new Set(
        (Array.isArray(list) ? list : []).map(p => p.jobCode || p.job_code).filter(Boolean)
      );
      setUsedJobCodes(codes);
    } catch {}
  };

  const handleCeipalSearchChange = (val) => {
    setCeipalSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => loadCeipalJobs(1, val, ceipalPageSize), 400);
  };

  const normalizeAts = (s) => String(s ?? '').replace(/[‒-―−]/g, '-').trim();

  const handleSelectCeipalJob = (job) => {
    setSelectedCeipalJob(job);
    const title = normalizeAts(job.position_title);
    setPositionName(title);
    setJobCode(job.job_code || '');
    setAiJobRole(title);
    setAiExperience(job.experience || '');
    setAiLocation(normalizeAts(job.city || job.country || ''));
    setFieldErrors(p => ({ ...p, positionName: undefined, jobCode: undefined, ceipalJob: undefined }));
    // Scroll the details section into view
    setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  // ── ATS helpers (Workday + Greenhouse) ────────────────────────────────────
  const atsApiBase = (src) => src === 'greenhouse' ? '/greenhouse' : '/workday';
  const atsLabel   = (src) => src === 'greenhouse' ? 'Greenhouse'  : 'Workday';

  const loadAtsJobs = async (page, search, company, src = source) => {
    setAtsLoading(true);
    try {
      const params = new URLSearchParams({ page: page || 1, search: search || '' });
      if (company && company !== 'all') params.set('company', company);
      const resp = await api.get(`${atsApiBase(src)}/jobs?${params.toString()}`);
      if (resp.data?.success) {
        setAtsJobs(resp.data.data || []);
        const total = resp.data.totalCount || 0;
        setAtsTotal(total);
        setAtsTotalPages(Math.max(1, resp.data.totalPages || 1));
        setAtsPage(page || 1);
      }
    } catch { toast.error(`Failed to load ${atsLabel(src)} jobs`); }
    finally { setAtsLoading(false); }
  };

  const loadAtsCompanies = async (src = source) => {
    try {
      const resp = await api.get(`${atsApiBase(src)}/companies`);
      if (resp.data?.success) setAtsCompanies(resp.data.data || []);
    } catch {}
  };

  const handleAtsSearchChange = (val) => {
    setAtsSearch(val);
    clearTimeout(atsSearchTimerRef.current);
    atsSearchTimerRef.current = setTimeout(
      () => loadAtsJobs(1, val, atsCompanyFilter), 400);
  };

  const handleSelectAtsJob = (job) => {
    setSelectedAtsJob(job);
    const title = job.title || '';
    setPositionName(title);
    setJobCode(generateJobCode(title));
    setAiJobRole(title);
    setAiLocation(job.location || '');
    setFieldErrors(p => ({ ...p, positionName: undefined, jobCode: undefined, atsJob: undefined }));
    setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  // ── Step 1 validation ──────────────────────────────────────────────────────
  const validateStep1 = () => {
    const errs = {};
    if (!positionName.trim()) errs.positionName = 'Required';
    if (!clientName.trim())   errs.clientName   = 'Required';
    if (!jobCode.trim())      errs.jobCode      = 'Required';
    if (source === 'ceipal' && !selectedCeipalJob) errs.ceipalJob = 'Please select a Ceipal job first';
    if ((source === 'workday' || source === 'greenhouse') && !selectedAtsJob) errs.atsJob = `Please select a ${atsLabel(source)} job first`;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleGoToStep2 = () => {
    if (!validateStep1()) return;
    setStep(2);
    if (source === 'ceipal' && selectedCeipalJob) fetchCeipalJd(selectedCeipalJob);
    if ((source === 'workday' || source === 'greenhouse') && selectedAtsJob) fetchAtsJd(selectedAtsJob);
  };

  const handleGoToStep3 = () => {
    if (!jdContent.trim()) { toast.error('Please create or upload a job description first'); return; }
    setStep(3);
  };

  /** Step 2 back: JD preview → job title form; job title form → position details. */
  const handleStep2Back = () => {
    if (showDiff) {
      setShowDiff(false);
      return;
    }
    if (isEditing) {
      setIsEditing(false);
      return;
    }
    if (jdContent) {
      setJdContent('');
      setJdType('');
      return;
    }
    setStep(1);
  };

  // ── Ceipal JD fetch ────────────────────────────────────────────────────────
  const fetchCeipalJd = async (job) => {
    setFetchingCeipalJd(true);
    try {
      let jd = job.job_description || job.description || '';
      if (!jd) {
        const resp = await api.get(`/ceipal/job-description/${job.job_id || job.id}`);
        if (resp.data?.success && resp.data.job_description) jd = resp.data.job_description;
      }
      if (jd) {
        setJdContent(normalizeJdHtmlFromSource(jd)); setJdType('ceipal');
      } else {
        toast.info(aiJdOn ? 'No JD found in Ceipal. You can generate one with AI.' : 'No JD found in Ceipal. Write or upload one.');
        setJdTab(defaultJdTab);
      }
    } catch { toast.error('Failed to fetch JD from Ceipal'); }
    finally { setFetchingCeipalJd(false); }
  };

  // ── ATS JD fetch (Workday + Greenhouse) ───────────────────────────────────
  const fetchAtsJd = async (job) => {
    setFetchingAtsJd(true);
    const label = atsLabel(source);
    try {
      const resp = await api.get(`${atsApiBase(source)}/jobs/${encodeURIComponent(job.id)}`);
      const jd = resp.data?.data?.jobDescription || '';
      if (jd && jd.trim()) {
        setJdContent(normalizeJdHtmlFromSource(jd));
        setJdType(source);
      } else {
        toast.info(aiJdOn ? `No JD found in ${label}. You can generate one with AI.` : `No JD found in ${label}. Write or upload one.`);
        setJdTab(defaultJdTab);
      }
    } catch { toast.error(`Failed to fetch JD from ${label}`); }
    finally { setFetchingAtsJd(false); }
  };

  // ── AI JD generation ───────────────────────────────────────────────────────
  const extractJd = (data) => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    return data.generatedJd || data.job_description || data.jdText || data.content || '';
  };

  const handleGenerateAiJd = async () => {
    if (!aiJdOn) { toast.info(AI_JD_OFF_MESSAGE); return; }
    const role  = aiJobRole.trim();
    const quick = aiQuickInput.trim();
    if (!role && !quick) { toast.error('Enter a job role or quick description'); return; }
    const jdErrs = {};
    if (!aiExperience.trim()) jdErrs.aiExperience = 'Required';
    if (!aiLocation.trim())   jdErrs.aiLocation   = 'Required';
    if (Object.keys(jdErrs).length) { setAiFormErrors(jdErrs); return; }
    setAiFormErrors({});
    setGeneratingJd(true);
    try {
      let data;
      if (role) {
        const resp = await api.post('/job-descriptions/preview/structured', {
          jobTitle:          role,
          experienceInYears: aiExperience || '1-3',
          preferredLocation: aiLocation   || '',
          quickInput:        quick,
          positionType:      aiPositionType || 'Permanent Position',
          companyName:       aiCompanyName || clientName.trim() || '',
          aboutCompany:      aboutCompany.trim(),
        });
        data = resp.data;
      } else {
        const resp = await api.post('/job-descriptions/preview/single', { jobInput: quick });
        data = resp.data;
      }
      if (data?.success === false) { toast.error(data.message || 'Failed to generate JD'); return; }
      const jd = extractJd(data);
      if (jd.trim()) {
        setJdContent(normalizeJdForStorage(jd));
        setJdType('ai'); setShowDiff(false);
      } else {
        toast.error('AI returned an empty response. Please try again.');
      }
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to generate JD'); }
    finally { setGeneratingJd(false); }
  };

  // ── Manual JD ──────────────────────────────────────────────────────────────
  const handleUseManualJd = () => {
    const text = manualJdText.trim();
    if (text.length < 30) { toast.error('Please write the job description (at least a few lines).'); return; }
    setJdContent(normalizeJdForStorage(text));
    setJdType('manual'); setShowDiff(false);
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const handleFileSelect = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf','doc','docx','txt'].includes(ext)) { toast.error('Supported: PDF, DOC, DOCX, TXT'); return; }
    setSelectedFile(file);
  };

  const handleExtractJd = async () => {
    if (!selectedFile) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      const resp = await api.post('/job-descriptions/extract-text', fd);
      if (resp.data?.success && resp.data?.extractedText) {
        setJdContent(resp.data.extractedText.replace(/\\n/g,'\n'));
        setJdType('upload'); setSelectedFile(null); setShowDiff(false);
      } else { toast.error(resp.data?.error || 'Failed to extract text'); }
    } catch { toast.error('Failed to upload file'); }
    finally { setUploadingFile(false); }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleStartEdit = () => { setEditContent(jdContent); setIsEditing(true); };
  const handleSaveEdit  = () => { setJdContent(editContent); setIsEditing(false); };

  // ── Regenerate with comments ───────────────────────────────────────────────
  const handleRegenerate = async () => {
    if (!aiJdOn) { toast.info(AI_JD_OFF_MESSAGE); return; }
    if (!regenComment.trim()) { toast.error('Please enter feedback for the AI'); return; }
    setRegenerating(true);
    try {
      const resp = await api.post('/job-descriptions/preview/single', {
        jobInput:
          `Please regenerate this Job Description based on the following feedback:\n\nFeedback: ${regenComment}\n\n` +
          `Preserve role-specific skills, responsibilities, and requirements while incorporating the feedback.\n\n` +
          `ORIGINAL JD:\n${jdContent}`,
      });
      const jd = extractJd(resp.data);
      if (jd.trim()) {
        const cleaned = normalizeJdForStorage(jd);
        setOriginalJd(jdContent); setRegenJd(cleaned);
        setShowDiff(true); setShowRegenModal(false); setRegenComment('');
      } else { toast.error('Regeneration returned empty result.'); }
    } catch (err) { toast.error(err?.response?.data?.message || 'Regeneration failed'); }
    finally { setRegenerating(false); }
  };

  const handlePickDiff = (which) => {
    setJdContent(which === 'original' ? originalJd : regenJd);
    if (which === 'regenerated') setJdType('ai');
    setShowDiff(false); setOriginalJd(''); setRegenJd('');
    toast.success(`${which === 'original' ? 'Original' : 'Regenerated'} JD selected`);
  };

  // ── Create position ────────────────────────────────────────────────────────
  const handleCreateNow = async () => {
    setCreating(true);
    try {
      const posResp = await api.post('/positions', {
        positionName: positionName.trim(),
        clientName:   clientName.trim(),
        jobCode:      jobCode.trim(),
        ...(currentUser ? {
          createdBy:      currentUser.name || currentUser.displayName || currentUser.email || currentUser.upn || '',
          createdByEmail: currentUser.email || currentUser.upn || '',
        } : {}),
      });
      const positionId = posResp.data?.positionId;
      if (!positionId) throw new Error('No position ID returned');

      // Always persist job family to the catalog before linking the position.
      let roleKey = null;
      try {
        const resolved = await autoFetchJobFamilyForPosition({
          title: positionName.trim(),
          jdText: jdContent,
          experience: getPositionExperience(),
          generateSkills: true,
        });
        if (resolved.roleKey && isValidRoleKey(resolved.roleKey)) {
          roleKey = resolved.roleKey;
        } else if (confirmJfPreview?.sectorId && confirmJfPreview?.pendingRoleTitle) {
          const seated = await ensureRoleSeatedInJobFamily({
            title: confirmJfPreview.pendingRoleTitle,
            sectorId: confirmJfPreview.sectorId,
            familyId: confirmJfPreview.familyId || '',
            jdText: jdContent,
            experience: getPositionExperience(),
            generateSkills: true,
          });
          if (seated.roleKey && isValidRoleKey(seated.roleKey)) roleKey = seated.roleKey;
        } else if (confirmJfRoleKey && isValidRoleKey(confirmJfRoleKey)) {
          roleKey = confirmJfRoleKey;
        } else {
          const fallback = await resolveRoleKeyForPosition(positionName.trim(), {
            userSelectedKey: selectedJobFamilyKey,
            userTouched: jobFamilyTouchedRef.current,
            jdText: jdContent,
            experience: getPositionExperience(),
          });
          if (fallback && isValidRoleKey(fallback)) roleKey = fallback;
        }
        if (roleKey) {
          setSelectedJobFamilyKey(roleKey);
          setConfirmJfRoleKey(roleKey);
          setConfirmJfPreview(null);
        }
      } catch (e) {
        if (confirmJfPreview?.sectorId) {
          try {
            const seated = await ensureRoleSeatedInJobFamily({
              title: confirmJfPreview.pendingRoleTitle || positionName.trim(),
              sectorId: confirmJfPreview.sectorId,
              familyId: confirmJfPreview.familyId || '',
              jdText: jdContent,
              experience: getPositionExperience(),
              generateSkills: true,
            });
            if (seated.roleKey && isValidRoleKey(seated.roleKey)) roleKey = seated.roleKey;
          } catch {
            roleKey = confirmJfRoleKey && isValidRoleKey(confirmJfRoleKey) ? confirmJfRoleKey : null;
          }
        } else {
          roleKey = confirmJfRoleKey && isValidRoleKey(confirmJfRoleKey) ? confirmJfRoleKey : null;
        }
      }

      if (roleKey) {
        setPositionJobFamilyKey(positionId, roleKey);
        rememberLastCreatedRoleKey(roleKey);
      }

      const srcMap = { ai:'AI_GENERATED', upload:'UPLOADED', ceipal:'CEIPAL_IMPORT', manual:'MANUAL' };
      const attachResp = await api.post(`/job-descriptions/position/${positionId}/attach`, {
        jdText:     normalizeJdForStorage(jdContent),
        sourceType: srcMap[jdType] || 'AI_GENERATED',
      });

      const jdId = attachResp.data?.jdId;
      if (jdId) {
        const { skills } = await ensurePositionSkillWeightages({
          positionId,
          positionTitle: positionName.trim(),
          jdText: jdContent,
          experience: getPositionExperience(),
          roleKey: roleKey || '',
        });
        if (skills.length) {
          await syncJobFamilySkillsToJd(api, jdId, skills);
        }
      }

      toast.success(`Position "${positionName.trim()}" created successfully!`);
      onSuccess && onSuccess({ positionId, createdByName: currentUser?.name || currentUser?.displayName || currentUser?.email || '' });
      onHide();
      navigate(`/stack-ranking/${positionId}`, { state: { _refresh: Date.now() } });
    } catch (err) {
      const status = err?.response?.status;
      const msg    = err?.response?.data?.message || err.message || '';
      if (status === 409 || (msg.toLowerCase().includes('job code') && msg.toLowerCase().includes('already'))) {
        toast.error('This Job Code already exists. Please go back and use a unique code.');
      } else {
        toast.error(msg || 'Failed to create position. Please try again.');
      }
    } finally { setCreating(false); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Render helpers
  // ═══════════════════════════════════════════════════════════════════════════

  const renderStep1 = () => {
    // For Ceipal: show the full job-picker table
    if (source === 'ceipal') {
      const filteredJobs = ceipalJobs.filter(job => {
        const isUsed = usedJobCodes.has(job.job_code);
        if (ceipalFilter === 'available') return !isUsed;
        if (ceipalFilter === 'used') return isUsed;
        return true;
      });
      const selCode = selectedCeipalJob?.job_code || selectedCeipalJob?.job_id;
      // visible page numbers around current
      const pgNums = [];
      for (let p = 1; p <= ceipalTotalPages; p++) {
        if (p === 1 || p === ceipalTotalPages || Math.abs(p - ceipalPage) <= 1) pgNums.push(p);
      }
      const pgNumsWithDots = [];
      pgNums.forEach((p, i) => {
        if (i > 0 && p - pgNums[i-1] > 1) pgNumsWithDots.push('…');
        pgNumsWithDots.push(p);
      });

      return (
        <div>
          {/* Search */}
          <input
            type="text"
            className="npm-inp"
            value={ceipalSearch}
            onChange={e => handleCeipalSearchChange(e.target.value)}
            placeholder="Search by job title or job code…"
            style={{ marginBottom:10 }}
          />

          {/* Filter tabs */}
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            {[['all','All'],['available','Available'],['used','Already Used']].map(([val,lbl]) => (
              <button key={val} className={`cej-ftab${ceipalFilter===val?' on':''}`}
                onClick={() => setCeipalFilter(val)}>{lbl}</button>
            ))}
          </div>

          {/* Selected job info banner */}
          {selectedCeipalJob && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(0,65,194,0.07)', border:'1.5px solid rgba(0,65,194,0.25)', borderRadius:9, padding:'9px 14px', marginBottom:10 }}>
              <div>
                <span style={{ fontSize:11, color:'#0041C2', fontWeight:700 }}>Selected: </span>
                <span style={{ fontSize:12, fontWeight:700, color:'var(--ds-text)' }}>{selectedCeipalJob.position_title}</span>
                <span style={{ fontSize:11, color:'var(--ds-text2)', marginLeft:8 }}>{selectedCeipalJob.job_code}</span>
              </div>
              <button className="npm-btn npm-btn-ghost" style={{ padding:'3px 10px', fontSize:11 }}
                onClick={() => setSelectedCeipalJob(null)}>Change</button>
            </div>
          )}

          {/* Table */}
          <div className="cej-tbl">
            <div className="cej-head">
              <span className="cej-th">Job Code</span>
              <span className="cej-th">Position Title</span>
              <span className="cej-th">Location</span>
              <span className="cej-th">Status</span>
              <span className="cej-th">Action</span>
            </div>

            {ceipalLoading ? (
              <div style={{ padding:28, textAlign:'center' }}>
                <Spinner animation="border" size="sm" style={{ color:'#4D86E8' }}/>
                <span style={{ marginLeft:8, fontSize:12, color:'var(--ds-muted)' }}>Loading Ceipal jobs…</span>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div style={{ padding:28, textAlign:'center', color:'var(--ds-muted)', fontSize:12 }}>No jobs found</div>
            ) : filteredJobs.map(job => {
              const jid = job.job_id || job.id || job.job_code;
              const isUsed = usedJobCodes.has(job.job_code);
              const isSel  = !!selCode && selCode === job.job_code;
              const skills = job.skills || job.key_skills || '';
              const loc    = [job.city, job.country].filter(Boolean).join(', ') || job.location || '—';
              return (
                <div key={jid} className={`cej-row${isUsed?' used-row':''}`}>
                  {/* Job Code */}
                  <div><span className="cej-code">{job.job_code || '—'}</span></div>
                  {/* Title + skills */}
                  <div style={{ paddingRight:10 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'var(--ds-text)', lineHeight:1.3 }}>{job.position_title}</div>
                    {skills && (
                      <div style={{ fontSize:11, color:'var(--ds-text2)', marginTop:2 }}>
                        Skills: {skills.length > 60 ? skills.slice(0,60)+'…' : skills}
                      </div>
                    )}
                    {job.experience && (
                      <div style={{ fontSize:11, color:'var(--ds-text2)', marginTop:1 }}>Exp: {job.experience} yrs</div>
                    )}
                  </div>
                  {/* Location */}
                  <div style={{ fontSize:12, color:'var(--ds-text2)', display:'flex', alignItems:'center', gap:4 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {loc}
                  </div>
                  {/* Status */}
                  <div>
                    {isUsed
                      ? <span style={{ background:'rgba(239,68,68,0.12)', color:'#EF4444', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>Used</span>
                      : <span style={{ background:'rgba(16,185,129,0.12)', color:'#10B981', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>Active</span>
                    }
                  </div>
                  {/* Action */}
                  <div>
                    <button
                      className={`cej-sel-btn${isSel?' sel-active':''}`}
                      disabled={isUsed}
                      onClick={() => !isUsed && handleSelectCeipalJob(job)}
                    >
                      {isUsed ? 'Already Used' : isSel ? '✓ Selected' : '✓ Select'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination footer */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:12, flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:12, color:'var(--ds-text2)' }}>Show:</span>
              <select
                className="npm-sel"
                style={{ width:'auto', padding:'4px 10px', fontSize:12 }}
                value={ceipalPageSize}
                onChange={e => {
                  const ps = Number(e.target.value);
                  setCeipalPageSize(ps);
                  loadCeipalJobs(1, ceipalSearch, ps);
                }}
              >
                {[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span style={{ fontSize:12, color:'var(--ds-text2)' }}>
                Showing {ceipalTotal === 0 ? 0 : (ceipalPage-1)*ceipalPageSize+1}–{Math.min(ceipalPage*ceipalPageSize, ceipalTotal)} of {ceipalTotal} Ceipal jobs
              </span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <button className="npm-pg-btn" disabled={ceipalPage===1}
                onClick={() => loadCeipalJobs(ceipalPage-1, ceipalSearch)}>‹ Prev</button>
              {pgNumsWithDots.map((p, i) =>
                p === '…'
                  ? <span key={`d${i}`} style={{ fontSize:12, color:'var(--ds-muted)', padding:'0 2px' }}>…</span>
                  : <button key={p} className={`npm-pg-btn${p===ceipalPage?' cur':''}`}
                      onClick={() => loadCeipalJobs(p, ceipalSearch)}>{p}</button>
              )}
              <button className="npm-pg-btn" disabled={ceipalPage>=ceipalTotalPages}
                onClick={() => loadCeipalJobs(ceipalPage+1, ceipalSearch)}>Next ›</button>
            </div>
          </div>

          {fieldErrors.ceipalJob && <div className="npm-err" style={{ marginTop:8 }}>{fieldErrors.ceipalJob}</div>}

          {/* Position details (auto-filled from selected job) */}
          {selectedCeipalJob && (
            <div ref={detailsRef} className="npm-form-grid cej-details-highlight" style={{ marginTop:16 }}>
              <div className="npm-row-2">
                <div>
                  <label className="npm-lbl">Position Name</label>
                  <input type="text" className={`npm-inp${fieldErrors.positionName?' err':''}`} value={positionName} readOnly />
                  {fieldErrors.positionName && <div className="npm-err">{fieldErrors.positionName}</div>}
                </div>
                <div>
                  <label className="npm-lbl">Job Code</label>
                  <input type="text" className="npm-inp" value={jobCode} readOnly />
                </div>
              </div>
              <div>
                <label className="npm-lbl">Client Name</label>
                <input type="text" className={`npm-inp${fieldErrors.clientName?' err':''}`} value={clientName}
                  onChange={e => { setClientName(e.target.value); setFieldErrors(p => ({...p, clientName:undefined})); }}
                  placeholder="e.g. Acme Corporation" />
                {fieldErrors.clientName && <div className="npm-err">{fieldErrors.clientName}</div>}
              </div>
            </div>
          )}
        </div>
      );
    }

    // ATS mode (Workday or Greenhouse) — job picker table
    if (source === 'workday' || source === 'greenhouse') {
      const atsPgNums = [];
      for (let p = 1; p <= atsTotalPages; p++) {
        if (p === 1 || p === atsTotalPages || Math.abs(p - atsPage) <= 1) atsPgNums.push(p);
      }
      const atsPgWithDots = [];
      atsPgNums.forEach((p, i) => {
        if (i > 0 && p - atsPgNums[i-1] > 1) atsPgWithDots.push('…');
        atsPgWithDots.push(p);
      });
      const lbl = atsLabel(source);

      return (
        <div>
          {/* Search + company filter row */}
          <div style={{ display:'flex', gap:8, marginBottom:10 }}>
            <input
              type="text"
              className="npm-inp"
              value={atsSearch}
              onChange={e => handleAtsSearchChange(e.target.value)}
              placeholder={`Search ${lbl} jobs by title, location, department…`}
              style={{ flex:1 }}
            />
            {atsCompanies.length > 0 && (
              <select
                className="npm-sel"
                style={{ width:'auto', minWidth:150, padding:'8px 12px', fontSize:12 }}
                value={atsCompanyFilter}
                onChange={e => {
                  const comp = e.target.value;
                  setAtsCompanyFilter(comp);
                  loadAtsJobs(1, atsSearch, comp);
                }}
              >
                <option value="all">All Companies</option>
                {atsCompanies.map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Selected job banner */}
          {selectedAtsJob && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(0,65,194,0.07)', border:'1.5px solid rgba(0,65,194,0.25)', borderRadius:9, padding:'9px 14px', marginBottom:10 }}>
              <div>
                <span style={{ fontSize:11, color:'#0041C2', fontWeight:700 }}>Selected: </span>
                <span style={{ fontSize:12, fontWeight:700, color:'var(--ds-text)' }}>{selectedAtsJob.title}</span>
                <span style={{ fontSize:11, color:'var(--ds-text2)', marginLeft:8 }}>{selectedAtsJob.company}</span>
              </div>
              <button className="npm-btn npm-btn-ghost" style={{ padding:'3px 10px', fontSize:11 }}
                onClick={() => setSelectedAtsJob(null)}>Change</button>
            </div>
          )}

          {/* Table */}
          <div className="cej-tbl">
            <div className="cej-head" style={{ gridTemplateColumns:'1fr 140px 120px 100px' }}>
              <span className="cej-th">Position Title</span>
              <span className="cej-th">Company</span>
              <span className="cej-th">Location</span>
              <span className="cej-th">Action</span>
            </div>

            {atsLoading ? (
              <div style={{ padding:28, textAlign:'center' }}>
                <Spinner animation="border" size="sm" style={{ color:'#4D86E8' }}/>
                <span style={{ marginLeft:8, fontSize:12, color:'var(--ds-muted)' }}>Loading {lbl} jobs…</span>
              </div>
            ) : atsJobs.length === 0 ? (
              <div style={{ padding:28, textAlign:'center', color:'var(--ds-muted)', fontSize:12 }}>No jobs found</div>
            ) : atsJobs.map((job, idx) => {
              const isSel = selectedAtsJob?.id === job.id;
              return (
                <div key={job.id || idx} className="cej-row" style={{ gridTemplateColumns:'1fr 140px 120px 100px' }}>
                  <div style={{ paddingRight:10 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'var(--ds-text)', lineHeight:1.3 }}>{job.title}</div>
                    {job.department && (
                      <div style={{ fontSize:11, color:'var(--ds-text2)', marginTop:2 }}>{job.department}</div>
                    )}
                    {job.postedOn && (
                      <div style={{ fontSize:10, color:'var(--ds-muted)', marginTop:1 }}>{job.postedOn}</div>
                    )}
                  </div>
                  <div style={{ fontSize:12, color:'var(--ds-text2)' }}>{job.company || '—'}</div>
                  <div style={{ fontSize:12, color:'var(--ds-text2)', display:'flex', alignItems:'center', gap:4 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {job.location || '—'}
                  </div>
                  <div>
                    <button
                      className={`cej-sel-btn${isSel ? ' sel-active' : ''}`}
                      onClick={() => handleSelectAtsJob(job)}
                    >
                      {isSel ? '✓ Selected' : '✓ Select'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination footer */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:12, flexWrap:'wrap', gap:8 }}>
            <span style={{ fontSize:12, color:'var(--ds-text2)' }}>
              {atsTotal === 0 ? '0' : `${(atsPage-1)*20+1}–${Math.min(atsPage*20, atsTotal)}`} of {atsTotal} {lbl} jobs
            </span>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <button className="npm-pg-btn" disabled={atsPage===1}
                onClick={() => loadAtsJobs(atsPage-1, atsSearch, atsCompanyFilter)}>‹ Prev</button>
              {atsPgWithDots.map((p, i) =>
                p === '…'
                  ? <span key={`d${i}`} style={{ fontSize:12, color:'var(--ds-muted)', padding:'0 2px' }}>…</span>
                  : <button key={p} className={`npm-pg-btn${p===atsPage?' cur':''}`}
                      onClick={() => loadAtsJobs(p, atsSearch, atsCompanyFilter)}>{p}</button>
              )}
              <button className="npm-pg-btn" disabled={atsPage>=atsTotalPages}
                onClick={() => loadAtsJobs(atsPage+1, atsSearch, atsCompanyFilter)}>Next ›</button>
            </div>
          </div>

          {fieldErrors.atsJob && <div className="npm-err" style={{ marginTop:8 }}>{fieldErrors.atsJob}</div>}

          {/* Position details (auto-filled from selected job) */}
          {selectedAtsJob && (
            <div ref={detailsRef} className="npm-form-grid cej-details-highlight" style={{ marginTop:16 }}>
              <div className="npm-row-2">
                <div>
                  <label className="npm-lbl">Position Name</label>
                  <input type="text" className="npm-inp" value={positionName} readOnly />
                </div>
                <div>
                  <label className="npm-lbl">Job Code</label>
                  <input type="text" className="npm-inp" value={jobCode} readOnly />
                </div>
              </div>
              <div>
                <label className="npm-lbl">Client Name</label>
                <input type="text" className={`npm-inp${fieldErrors.clientName?' err':''}`} value={clientName}
                  onChange={e => { setClientName(e.target.value); setFieldErrors(p => ({...p, clientName:undefined})); }}
                  placeholder="e.g. Acme Corporation" />
                {fieldErrors.clientName && <div className="npm-err">{fieldErrors.clientName}</div>}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Manual mode — simple form
    return (
      <div className="npm-form-grid">
        <div>
          <label className="npm-lbl">Position Name</label>
          <input type="text" className={`npm-inp${fieldErrors.positionName ? ' err' : ''}`}
            value={positionName}
            onChange={e => {
              const filtered = e.target.value.replace(/[^a-zA-Z0-9 /\\&\-]/g, '');
              setPositionName(filtered);
              setAiJobRole(filtered);
              if (filtered.trim()) setJobCode(generateJobCode(filtered));
              setFieldErrors(p => ({...p, positionName:undefined, jobCode:undefined}));
            }}
            placeholder="e.g. Senior React Developer"
          />
          <div style={{ fontSize:10, color:'var(--ds-muted)', marginTop:4 }}>Allowed special characters: / \ &amp; -</div>
          {fieldErrors.positionName && <div className="npm-err">{fieldErrors.positionName}</div>}
        </div>
        <div>
          <label className="npm-lbl">Client Name</label>
          <input type="text" className={`npm-inp${fieldErrors.clientName ? ' err' : ''}`}
            value={clientName}
            onChange={e => { setClientName(e.target.value); setFieldErrors(p => ({...p, clientName:undefined})); }}
            placeholder="e.g. Acme Corporation"
          />
          {fieldErrors.clientName && <div className="npm-err">{fieldErrors.clientName}</div>}
        </div>
        <div>
          <label className="npm-lbl">Job Code / Position Code</label>
          <input type="text" className={`npm-inp${fieldErrors.jobCode ? ' err' : ''}`}
            value={jobCode}
            onChange={e => { setJobCode(e.target.value); setFieldErrors(p => ({...p, jobCode:undefined})); }}
            placeholder="HIC - SRD-123"
          />
          {fieldErrors.jobCode && <div className="npm-err">{fieldErrors.jobCode}</div>}
        </div>
      </div>
    );
  };

  const renderAiForm = () => (
    <div className="npm-form-grid">
      <div>
        <label className="npm-lbl">Job Role / Title *</label>
        <input type="text" className="npm-inp" value={aiJobRole}
          onChange={e => setAiJobRole(e.target.value)}
          placeholder="e.g. Senior React Developer" />
      </div>
      <div className="npm-row-2">
        <div>
          <label className="npm-lbl">Experience (years) *</label>
          <input type="text" className={`npm-inp${aiFormErrors.aiExperience ? ' err' : ''}`} value={aiExperience}
            onChange={e => { setAiExperience(e.target.value); setAiFormErrors(p => ({...p, aiExperience:undefined})); }}
            placeholder="e.g. 3-5" />
          {aiFormErrors.aiExperience && <div className="npm-err">{aiFormErrors.aiExperience}</div>}
        </div>
        <div>
          <label className="npm-lbl">Work Location *</label>
          <input type="text" className={`npm-inp${aiFormErrors.aiLocation ? ' err' : ''}`} value={aiLocation}
            onChange={e => { setAiLocation(e.target.value); setAiFormErrors(p => ({...p, aiLocation:undefined})); }}
            placeholder="e.g. Remote / Austin, TX" />
          {aiFormErrors.aiLocation && <div className="npm-err">{aiFormErrors.aiLocation}</div>}
        </div>
      </div>
      <div className="npm-row-2">
        <div>
          <label className="npm-lbl">Position Type</label>
          <select className="npm-sel" value={aiPositionType} onChange={e => setAiPositionType(e.target.value)}>
            <option value="Permanent Position">Permanent Position</option>
            <option value="Contract">Contract</option>
            <option value="Contract to Hire">Contract to Hire</option>
            <option value="Internship">Internship</option>
            <option value="Part-Time">Part-Time</option>
          </select>
        </div>
        <div>
          <label className="npm-lbl">Company Name</label>
          <input type="text" className="npm-inp" value={aiCompanyName}
            onChange={e => setAiCompanyName(e.target.value)}
            placeholder={clientName || 'e.g. Feuji Inc.'} />
        </div>
      </div>
      <div>
        <label className="npm-lbl">About Company <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, color:'var(--ds-muted)', fontSize:10 }}>(optional — shapes the company intro in the JD)</span></label>
        <textarea className="npm-textarea" style={{ height:72, resize:'vertical' }}
          value={aboutCompany}
          onChange={e => setAboutCompany(e.target.value)}
          placeholder="e.g. Feuji is a technology solutions company specializing in AI, cloud, and data engineering…"
        />
      </div>
      <div>
        <label className="npm-lbl">Additional Notes / Quick Input (Optional)</label>
        <textarea className="npm-textarea" style={{ height:90, resize:'vertical' }}
          value={aiQuickInput}
          onChange={e => setAiQuickInput(e.target.value)}
          placeholder="Specific skills, certifications, or requirements to emphasize…"
        />
      </div>
      <div>
        <button className="npm-btn npm-btn-primary" disabled={generatingJd} onClick={handleGenerateAiJd}>
          {generatingJd ? <><Spinner animation="border" size="sm" style={{ marginRight:6 }}/>Generating…</> : '✦ Generate Job Description'}
        </button>
      </div>
    </div>
  );


  const renderManualForm = () => (
    <div>
      <label className="npm-lbl">Job description</label>
      <textarea
        className="npm-input"
        value={manualJdText}
        onChange={(e) => setManualJdText(e.target.value)}
        placeholder={'Write or paste the job description here.\n\nTip: use headings like "Responsibilities", "Requirements", "Nice to have".'}
        rows={14}
        style={{ width:'100%', minHeight:260, resize:'vertical', fontFamily:'inherit', lineHeight:1.5 }}
      />
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10 }}>
        <button className="npm-btn npm-btn-primary" onClick={handleUseManualJd} disabled={!manualJdText.trim()}>
          Use this JD
        </button>
      </div>
    </div>
  );

  const renderUploadForm = () => (
    <div>
      <div
        className={`npm-dropzone${dragActive ? ' drag' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={e => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]); }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div style={{ fontSize:32, marginBottom:10, color:'var(--ds-text2)' }}>↑</div>
        <div style={{ fontSize:13, fontWeight:600, color:'var(--ds-text)', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
          {selectedFile ? selectedFile.name : 'Drop file here or click to browse'}
        </div>
        <div style={{ fontSize:11, color:'var(--ds-text2)', marginTop:5 }}>PDF, DOC, DOCX, TXT supported</div>
      </div>
      <input type="file" ref={fileInputRef} style={{ display:'none' }}
        accept=".pdf,.doc,.docx,.txt"
        onChange={e => { if (e.target.files[0]) handleFileSelect(e.target.files[0]); e.target.value=''; }}
      />
      {selectedFile && (
        <div style={{ marginTop:12, display:'flex', justifyContent:'flex-end' }}>
          <button className="npm-btn npm-btn-primary" disabled={uploadingFile} onClick={handleExtractJd}>
            {uploadingFile ? <><Spinner animation="border" size="sm" style={{ marginRight:6 }}/>Extracting…</> : 'Extract & Use JD'}
          </button>
        </div>
      )}
    </div>
  );

  const renderJdActions = () => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
      <label className="npm-lbl" style={{ margin:0 }}>
        {jdType==='ceipal' ? 'JD from Ceipal' : jdType==='workday' ? 'JD from Workday' : jdType==='greenhouse' ? 'JD from Greenhouse' : jdType==='upload' ? 'Uploaded JD' : jdType==='manual' ? 'Written JD' : 'AI Generated JD'}
        <span style={{ marginLeft:8, fontSize:10, background:'var(--ds-badge-active-bg)', color:'var(--ds-badge-active-text)', border:'1px solid var(--ds-badge-active-border)', borderRadius:20, padding:'1px 7px', fontWeight:700, textTransform:'uppercase' }}>
          Ready
        </span>
      </label>
      <div style={{ display:'flex', gap:6 }}>
        <button className="npm-btn npm-btn-ghost" style={{ padding:'5px 11px', fontSize:11 }} onClick={handleStartEdit}>Edit</button>
        {aiJdOn && (
          <button className="npm-btn npm-btn-secondary" style={{ padding:'5px 11px', fontSize:11 }} onClick={() => { setOriginalJd(jdContent); setShowRegenModal(true); }}>Regenerate</button>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => {
    if (fetchingCeipalJd || fetchingAtsJd) return (
      <div style={{ textAlign:'center', padding:'56px 20px' }}>
        <Spinner animation="border" style={{ color:'#4D86E8' }}/>
        <p style={{ color:'var(--ds-muted)', marginTop:14, fontSize:13, marginBottom:0 }}>
          {fetchingAtsJd ? `Fetching JD from ${atsLabel(source)}…` : 'Fetching JD from Ceipal…'}
        </p>
      </div>
    );

    // Diff view — compare plain text so Ceipal HTML JDs render readably, not as raw tags
    if (showDiff) {
      const { leftDiff, rightDiff } = computeLineDiff(
        normalizeJdForExport(originalJd),
        normalizeJdForExport(regenJd),
      );
      const removedCount = leftDiff.filter(l => l.type === 'removed').length;
      const addedCount   = rightDiff.filter(l => l.type === 'added').length;
      return (
        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--ds-text)', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Select which JD to use</div>
              <div style={{ fontSize:11, color:'var(--ds-muted)', marginTop:3, display:'flex', gap:12 }}>
                <span style={{ color:'#EF4444', fontWeight:600 }}>− {removedCount} removed</span>
                <span style={{ color:'#D97706', fontWeight:600 }}>+ {addedCount} added</span>
              </div>
            </div>
            <button className="npm-btn npm-btn-ghost" style={{ padding:'4px 10px', fontSize:11 }} onClick={() => setShowDiff(false)}>← Back</button>
          </div>
          <div style={{ display:'flex', gap:12 }}>
            <div className="npm-diff-col">
              <div className="npm-diff-head" style={{ background:'rgba(239,68,68,0.07)', color:'#EF4444' }}>Original JD</div>
              <div className="npm-diff-body">
                {leftDiff.map((item, idx) => {
                  if (item.type === 'changed') return (
                    <div key={idx} className="npm-diff-line" style={{ background:'rgba(239,68,68,0.07)', borderLeft:'3px solid rgba(239,68,68,0.4)', paddingLeft:9 }}>
                      <span className="npm-diff-sign" style={{ color:'#EF4444' }}>~</span>
                      <span>{item.wordDiff.filter(s => s.type !== 'added').map((seg, si) =>
                        seg.type === 'removed'
                          ? <mark key={si} style={{ background:'rgba(239,68,68,0.32)', borderRadius:2, padding:'0 1px', color:'inherit' }}>{seg.text}</mark>
                          : <span key={si}>{seg.text}</span>
                      )}</span>
                    </div>
                  );
                  return (
                    <div key={idx} className={`npm-diff-line ${item.type}`}>
                      {item.type === 'removed'
                        ? <span className="npm-diff-sign" style={{ color:'#EF4444' }}>−</span>
                        : <span className="npm-diff-sign" style={{ color:'transparent' }}>·</span>
                      }
                      <span>{item.line}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ padding:'9px 12px', borderTop:'1px solid var(--ds-border)' }}>
                <button className="npm-btn npm-btn-secondary" style={{ width:'100%', justifyContent:'center', fontSize:11 }} onClick={() => handlePickDiff('original')}>Use Original</button>
              </div>
            </div>
            <div className="npm-diff-col">
              <div className="npm-diff-head" style={{ background:'rgba(234,179,8,0.07)', color:'#D97706' }}>Regenerated JD</div>
              <div className="npm-diff-body">
                {rightDiff.map((item, idx) => {
                  if (item.type === 'changed') return (
                    <div key={idx} className="npm-diff-line" style={{ background:'rgba(234,179,8,0.07)', borderLeft:'3px solid rgba(234,179,8,0.4)', paddingLeft:9 }}>
                      <span className="npm-diff-sign" style={{ color:'#D97706' }}>~</span>
                      <span>{item.wordDiff.filter(s => s.type !== 'removed').map((seg, si) =>
                        seg.type === 'added'
                          ? <mark key={si} style={{ background:'rgba(234,179,8,0.35)', borderRadius:2, padding:'0 1px', color:'inherit' }}>{seg.text}</mark>
                          : <span key={si}>{seg.text}</span>
                      )}</span>
                    </div>
                  );
                  return (
                    <div key={idx} className={`npm-diff-line ${item.type}`}>
                      {item.type === 'added'
                        ? <span className="npm-diff-sign" style={{ color:'#D97706' }}>+</span>
                        : <span className="npm-diff-sign" style={{ color:'transparent' }}>·</span>
                      }
                      <span>{item.line}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ padding:'9px 12px', borderTop:'1px solid var(--ds-border)' }}>
                <button className="npm-btn npm-btn-primary" style={{ width:'100%', justifyContent:'center', fontSize:11 }} onClick={() => handlePickDiff('regenerated')}>Use Regenerated</button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Edit view
    if (isEditing) return (
      <div>
        <label className="npm-lbl">Edit Job Description</label>
        <textarea className="npm-textarea edit-h" value={editContent} onChange={e => setEditContent(e.target.value)} />
        <div style={{ display:'flex', gap:8, marginTop:10, justifyContent:'flex-end' }}>
          <button className="npm-btn npm-btn-ghost" onClick={() => setIsEditing(false)}>Cancel</button>
          <button className="npm-btn npm-btn-primary" onClick={handleSaveEdit}>Save Changes</button>
        </div>
      </div>
    );

    // JD preview with actions
    if (jdContent) return (
      <div>
        {renderJdActions()}
        <div
          className="npm-jd-preview jd-preview-content"
          dangerouslySetInnerHTML={{ __html: formatJdPreviewHtml(jdContent) }}
        />
      </div>
    );

    // No JD yet — tabs
    const activeTab = (!aiJdOn && jdTab === 'ai') ? 'manual' : jdTab;
    const noJdTabs = (
      <div>
        <div className="npm-tab-bar">
          {aiJdOn && (
            <button className={`npm-tab${activeTab==='ai' ? ' on' : ''}`} onClick={() => setJdTab('ai')}>✦ Generate with AI</button>
          )}
          <button className={`npm-tab${activeTab==='manual' ? ' on' : ''}`} onClick={() => setJdTab('manual')}>✎ Write manually</button>
          <button className={`npm-tab${activeTab==='upload' ? ' on' : ''}`} onClick={() => setJdTab('upload')}>↑ Upload JD</button>
        </div>
        {!aiJdOn && (
          <div style={{ fontSize:12, color:'var(--ds-muted)', background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:8, padding:'8px 12px', marginBottom:14 }}>
            {AI_JD_OFF_MESSAGE}
          </div>
        )}
        {activeTab==='ai' ? renderAiForm() : activeTab==='manual' ? renderManualForm() : renderUploadForm()}
      </div>
    );

    if (source === 'ceipal') return (
      <div>
        <div style={{ fontSize:12, color:'var(--ds-muted)', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:8, padding:'9px 13px', marginBottom:18 }}>
          No JD was found in Ceipal for this position. {aiJdOn ? 'Generate one with AI, write it, or upload a file.' : 'Write it or upload a file.'}
        </div>
        {noJdTabs}
      </div>
    );

    return noJdTabs;
  };

  const renderStep3 = () => {
    const jdSrcLabel = { ai:'AI Generated', upload:'Uploaded (PDF/DOC)', ceipal:'Ceipal Import', manual:'Written manually' }[jdType] || 'Generated';
    const createdByLabel = currentUser?.name || currentUser?.displayName || currentUser?.email || 'Current User';
    const experienceLabel = getPositionExperience() || '—';

    return (
      <div>
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--ds-text)', fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom:4 }}>
            Review & Confirm
          </div>
          <div style={{ fontSize:12, color:'var(--ds-text2)' }}>
            Please review the details below. Click "Create Position" to finish.
          </div>
        </div>

        {/* Summary card */}
        <div style={{ background:'var(--ds-surface)', border:'1.5px solid var(--ds-border)', borderRadius:12, padding:'16px 20px', marginBottom:18 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--ds-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
            Position Details
          </div>
          {[
            { lbl:'Position Name',      val: positionName },
            { lbl:'Client Name',        val: clientName   },
            { lbl:'Job Code',           val: jobCode      },
            { lbl:'Experience',         val: experienceLabel },
            { lbl:'JD Source',          val: jdSrcLabel   },
            { lbl:'Created By',         val: createdByLabel },
          ].map(row => (
            <div key={row.lbl} className="npm-confirm-row">
              <div className="npm-confirm-lbl">{row.lbl}</div>
              <div className="npm-confirm-val">{row.val || '—'}</div>
            </div>
          ))}
        </div>

        {/* Job Family profile — auto-mapped; read-only unless user chooses Edit */}
        <div style={{ background:'var(--ds-surface)', border:'1.5px solid var(--ds-border)', borderRadius:12, padding:'16px 20px', marginBottom:18 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ds-muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>
              Job Family Profile
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              {confirmJfLoading && (
                <span style={{ fontSize:11, color:'var(--ds-text2)', display:'flex', alignItems:'center', gap:6 }}>
                  <Spinner animation="border" size="sm" />
                  Analyzing designation, JD &amp; experience against JOB_FAMILY catalog…
                </span>
              )}
              {!confirmJfLoading && (confirmJfFamilyCreated || confirmJfCreated) && (
                <span style={{ fontSize:11, fontWeight:600, color:'#059669', background:'rgba(5,150,105,0.1)', padding:'2px 8px', borderRadius:6 }}>
                  {confirmJfFamilyCreated && confirmJfCreated
                    ? 'AI-created family & designation'
                    : confirmJfFamilyCreated
                      ? 'AI-created family'
                      : 'AI-created designation'}
                </span>
              )}
              {!confirmJfLoading && (confirmJfRoleKey || confirmJfPreview || selectedJobFamilyKey) && (
                <button
                  type="button"
                  className="npm-btn npm-btn-ghost"
                  style={{ padding:'3px 10px', fontSize:11 }}
                  onClick={() => setEditingConfirmJobFamily((v) => !v)}
                >
                  {editingConfirmJobFamily ? 'Done' : 'Edit'}
                </button>
              )}
            </div>
          </div>

          {confirmJfLoading && !confirmJfRoleKey && !confirmJfPreview ? (
            <div style={{ fontSize:12, color:'var(--ds-text2)', lineHeight:1.5 }}>
              Comparing designation, JD, and experience against the JOB_FAMILY master catalog. Matching designations are linked to existing families; new designations are placed under the correct sector with AI-generated skill weightage.
            </div>
          ) : (confirmJfRoleKey || confirmJfPreview || selectedJobFamilyKey) ? (
            <JobFamilyTripleSelect
              roleKey={confirmJfRoleKey || selectedJobFamilyKey}
              preview={confirmJfPreview}
              idPrefix="npm-confirm-jf"
              readOnly={!editingConfirmJobFamily}
              onRoleKeyChange={(key) => {
                jobFamilyTouchedRef.current = true;
                setConfirmJfRoleKey(key);
                setConfirmJfPreview(null);
                setSelectedJobFamilyKey(key);
              }}
            />
          ) : (
            <div style={{ fontSize:12, color:'var(--ds-text2)' }}>
              Job Family profile could not be resolved yet. You can still create the position — linking will be attempted in the background.
            </div>
          )}
        </div>

        {/* JD preview */}
        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:9 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ds-muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>
              Job Description Preview
            </div>
            <button className="npm-btn npm-btn-ghost" style={{ padding:'3px 10px', fontSize:11 }}
              onClick={() => setStep(2)}>Edit JD</button>
          </div>
          <div
            className="npm-jd-preview jd-preview-content"
            style={{ height:180 }}
            dangerouslySetInnerHTML={{ __html: formatJdPreviewHtml(jdContent) }}
          />
        </div>
      </div>
    );
  };

  // ── Step indicator ─────────────────────────────────────────────────────────
  const stepDotClass = (n) => {
    if (n < step) return 'done';
    if (n === step) return 'active';
    return 'idle';
  };

  const STEP_LABELS = ['Position Details', 'Job Description', 'Confirm'];

  // ── Footer buttons ─────────────────────────────────────────────────────────
  const renderFooter = () => {
    if (step === 1) return (
      <>
        <button className="npm-btn npm-btn-ghost" onClick={onHide}>Cancel</button>
        <button className="npm-btn npm-btn-primary" onClick={handleGoToStep2}>
          Next: Job Description →
        </button>
      </>
    );
    if (step === 2) return (
      <>
        <button className="npm-btn npm-btn-ghost" onClick={handleStep2Back}>
          ← Back
        </button>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {!jdContent && !fetchingCeipalJd && (
            <span style={{ fontSize:11, color:'var(--ds-text2)', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Generate or upload a JD to continue
            </span>
          )}
          <button className="npm-btn npm-btn-primary" disabled={!jdContent || showDiff || isEditing} onClick={handleGoToStep3}>
            Next: Review →
          </button>
        </div>
      </>
    );
    return (
      <>
        <button className="npm-btn npm-btn-ghost" onClick={() => setStep(2)}>← Back</button>
        <button className="npm-btn npm-btn-primary" disabled={creating} onClick={handleCreateNow}>
          {creating ? <><Spinner animation="border" size="sm" style={{ marginRight:6 }}/>Creating…</> : '✓ Create Position'}
        </button>
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Main render
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <>
      <style>{STYLES}</style>

      {show && dragPos && createPortal(
        <div
          ref={dialogRef}
          style={{
            position: 'fixed',
            left: dragPos.x,
            top:  dragPos.y,
            width: Math.min((source === 'ceipal' || source === 'workday' || source === 'greenhouse') ? 940 : 820, window.innerWidth - 280),
            zIndex: 1055,
            background: 'var(--ds-card)',
            borderRadius: 14,
            border: '1px solid var(--ds-border)',
            boxShadow: '0 12px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,65,194,0.08)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '88vh',
            overflow: 'hidden',
            userSelect: dragging ? 'none' : undefined,
            color: 'var(--ds-text)',
            fontFamily: "'Plus Jakarta Sans',sans-serif",
          }}
        >
          {/* ── Header / drag handle ─────────────────────────────────────── */}
          <div
            className="npm-drag-handle"
            onMouseDown={onDragStart}
            style={{
              background: 'var(--ds-card)',
              padding: '20px 24px 0',
              cursor: dragging ? 'grabbing' : 'grab',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:38, height:38, borderRadius:10, background:'linear-gradient(135deg,#0041C2,#1A5FD4)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:'var(--ds-text)', lineHeight:1.2 }}>
                  New Position — {source==='ceipal' ? 'Import from Ceipal' : source==='workday' ? 'Import from Workday' : source==='greenhouse' ? 'Import from Greenhouse' : 'Create Manually'}
                </div>
                <div style={{ fontSize:11, color:'var(--ds-text2)', marginTop:3 }}>
                  Step {step} of 3: {STEP_LABELS[step-1]}
                </div>
              </div>
            </div>
            {/* Close × */}
            <button
              onClick={onHide}
              onMouseDown={e => e.stopPropagation()}
              style={{
                background: 'transparent',
                border: '1.5px solid var(--ds-border)',
                borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--ds-muted)',
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginLeft: 12,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color='var(--ds-text)'; e.currentTarget.style.background='var(--ds-hover)'; e.currentTarget.style.borderColor='var(--ds-border2)'; }}
              onMouseLeave={e => { e.currentTarget.style.color='var(--ds-muted)'; e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='var(--ds-border)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* ── Step indicator ─────────────────────────────────────────────── */}
          <div className="npm-steps" style={{ flexShrink:0 }}>
            {STEP_LABELS.map((lbl, i) => {
              const n = i + 1;
              return (
                <React.Fragment key={n}>
                  <div className="npm-step-wrap">
                    <div className={`npm-step-dot ${stepDotClass(n)}`}>
                      {n < step ? '✓' : n}
                    </div>
                    <span className="npm-step-label" style={{ color: n === step ? '#0041C2' : n < step ? '#10B981' : 'var(--ds-text2)' }}>
                      {lbl}
                    </span>
                  </div>
                  {i < STEP_LABELS.length - 1 && <div className="npm-step-line" style={{ background: n < step ? '#10B981' : 'var(--ds-border)' }} />}
                </React.Fragment>
              );
            })}
          </div>

          {/* ── Body ───────────────────────────────────────────────────────── */}
          <div style={{ background:'var(--ds-card)', padding:'20px 24px 24px', overflowY:'auto', flex:1 }}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </div>

          {/* ── Footer ─────────────────────────────────────────────────────── */}
          <div style={{ background:'var(--ds-card)', borderTop:'1px solid var(--ds-border)', padding:'12px 24px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            {renderFooter()}
          </div>
        </div>,
        document.body
      )}

      {/* ── Regenerate with comments mini-modal ─────────────────────────── */}
      <Modal show={showRegenModal} onHide={() => setShowRegenModal(false)} size="sm" centered>
        <Modal.Header closeButton style={{ background:'var(--ds-card)', borderColor:'var(--ds-border)', padding:'14px 18px' }}>
          <Modal.Title style={{ fontSize:14, fontWeight:700, color:'var(--ds-text)', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            Regenerate with Feedback
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background:'var(--ds-card)', padding:'14px 18px' }}>
          <label className="npm-lbl">Your feedback / comments</label>
          <textarea
            className="npm-textarea regen-h"
            value={regenComment}
            onChange={e => setRegenComment(e.target.value)}
            placeholder="e.g. Add more cloud tech requirements, remove education section, add leadership skills…"
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer style={{ background:'var(--ds-card)', borderColor:'var(--ds-border)', padding:'10px 18px', display:'flex', justifyContent:'space-between' }}>
          <button className="npm-btn npm-btn-ghost" onClick={() => setShowRegenModal(false)}>Cancel</button>
          <button className="npm-btn npm-btn-primary" disabled={regenerating} onClick={handleRegenerate}>
            {regenerating ? <><Spinner animation="border" size="sm" style={{ marginRight:6 }}/>Regenerating…</> : '✦ Regenerate'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
